import React, { useState, useEffect } from 'react';
import Button from './Button';
import CloseIcon from '../icons/CloseIcon';
import RightArrowIcon from '../icons/RightArrowIcon';
import MinusIcon from '../icons/MinusIcon';
import PlusIcon from '../icons/PlusIcon';

interface PointHistory {
  id: string;
  type: 'add' | 'subtract' | 'use' | 'oneTime' | 'weekly' | 'monthly';
  reason: string;
  amount: number;
  balance?: number;
  createdAt: Date;
  teacherId?: string;
  teacherName?: string;
  studentId?: string;
  studentName?: string;
}

interface StudentPointEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  studentName: string;
  currentPoints: number;
  pointHistory: PointHistory[];
  onSave: (adjustment: number, reason: string) => void;
  scale?: number;
}

const StudentPointEditDialog: React.FC<StudentPointEditDialogProps> = ({
  isOpen,
  onClose,
  studentName,
  currentPoints,
  pointHistory,
  onSave,
  scale = 1
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [adjustment, setAdjustment] = useState(0);
  const [reason, setReason] = useState('');

  // 모바일 감지
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 다이얼로그가 열릴 때마다 초기화
  useEffect(() => {
    if (isOpen) {
      setAdjustment(0);
      setReason('');
    }
  }, [isOpen]);

  // 빠른 조정 버튼
  const quickAdjust = (amount: number) => {
    setAdjustment(prev => prev + amount);
  };

  // 수정 후 포인트 계산
  const afterPoints = currentPoints + adjustment;

  // 완료 버튼 클릭
  const handleComplete = () => {
    if (adjustment !== 0) {
      onSave(adjustment, reason || '직접 수정');
    }
    onClose();
  };

  // 새로고침 (초기화)
  const handleReset = () => {
    setAdjustment(0);
    setReason('');
  };

  // 포인트 내역에 balance 계산 추가
  const historyWithBalance = pointHistory.map((item, index) => {
    // 현재 포인트부터 역순으로 계산
    let balance = currentPoints;
    
    // 이 항목 이후의 모든 내역을 역순으로 계산
    for (let i = index + 1; i < pointHistory.length; i++) {
      const laterItem = pointHistory[i];
      if (laterItem.type === 'add' || laterItem.type === 'oneTime') {
        balance -= laterItem.amount;
      } else if (laterItem.type === 'subtract' || laterItem.type === 'use') {
        balance += laterItem.amount;
      }
    }
    
    return {
      ...item,
      balance: item.balance !== undefined ? item.balance : balance
    };
  });

  // 포인트 내역을 월별로 그룹화
  const groupedHistory = historyWithBalance.reduce((acc, item) => {
    const date = new Date(item.createdAt);
    const monthKey = `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}`;
    
    if (!acc[monthKey]) {
      acc[monthKey] = [];
    }
    acc[monthKey].push(item);
    return acc;
  }, {} as Record<string, typeof historyWithBalance>);

  // 포맷팅 함수
  const formatPoints = (points: number) => {
    return points.toLocaleString('ko-KR');
  };

  const formatDate = (date: Date) => {
    const d = new Date(date);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000 // 모달 창 위에 표시
    }}>
      <div style={{
        background: 'var(--Color-background-normal, white)',
        borderRadius: '1rem',
        width: isMobile ? 'calc(100% - 40px)' : '28.125rem',
        maxWidth: isMobile ? '100%' : '28.125rem',
        height: isMobile ? 'calc(100vh - 40px)' : '42.8125rem',
        maxHeight: isMobile ? '100vh' : '42.8125rem',
        boxShadow: '0px 0.5rem 1rem rgba(0, 0, 0, 0.12)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transformOrigin: 'center center',
        transform: `scale(${scale})`,
        position: 'relative'
      }}>
        {/* 스크롤 가능한 컨텐츠 */}
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* 헤더 - 고정 */}
          <div>
            <div style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              padding: isMobile ? '1rem 1rem 1.25rem 1rem' : '1.5rem 1.75rem 2rem 1.75rem',
              gap: '0.5rem'
            }}>
              <div style={{
                flex: '1',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.125rem'
              }}>
                <h1 style={{
                  margin: 0,
                  fontSize: isMobile ? '1.125rem' : '1.25rem',
                  fontWeight: '600',
                  lineHeight: '150%',
                  color: 'var(--Color-text-normal, #161616)',
                  wordWrap: 'break-word'
                }}>
                  포인트 수정하기
                </h1>
                <span style={{
                  fontSize: isMobile ? '0.875rem' : '1rem',
                  fontWeight: '500',
                  lineHeight: '150%',
                  color: 'var(--Color-text-assistive, #989898)',
                  wordWrap: 'break-word'
                }}>
                  학생의 포인트 수정이 필요하신가요?
                </span>
              </div>
              
              {/* 닫기 버튼 */}
              <div style={{
                padding: '0.25rem',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={onClose}
              >
                <CloseIcon width={13.2} height={13.2} color="#0D0D0D" />
              </div>
            </div>
          </div>

          {/* 스크롤 가능한 메인 컨텐츠 */}
          <div 
            style={{
              flex: 1,
              overflowY: 'auto',
              position: 'relative'
            }}
          >
            <div style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: isMobile ? '1.5rem' : '2rem',
              padding: '0rem 0rem 1.5rem 0rem'
            }}>
            {/* 포인트 조정 영역 */}
            <div style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: isMobile ? '0rem 1rem' : '0rem 1.75rem',
              gap: isMobile ? '1.5rem' : '2.25rem'
            }}>
              {/* 포인트 카드 */}
              <div style={{
                width: isMobile ? '100%' : '24.625rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem'
              }}>
                {/* 학생 정보 & 포인트 표시 */}
                <div style={{
                  width: '100%',
                  background: 'var(--Color-fill-brand-color, #FF6F0F)',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem'
                }}>
                  {/* 학생 이름 */}
                  <div style={{
                    padding: '0.75rem 1rem 0 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{
                      fontSize: isMobile ? '1rem' : '1.125rem',
                      fontWeight: '600',
                      lineHeight: '150%',
                      color: 'white',
                      wordWrap: 'break-word'
                    }}>
                      {studentName}
                    </span>
                  </div>

                  {/* 포인트 비교 */}
                  <div style={{
                    width: '100%',
                    padding: '0.75rem 0.9375rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <div style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: isMobile ? '1.5rem' : '3.0625rem'
                    }}>
                      {/* 보유 포인트 */}
                      <div style={{
                        width: isMobile ? 'auto' : '5.4375rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.125rem'
                      }}>
                        <span style={{
                          fontSize: isMobile ? '0.75rem' : '0.8125rem',
                          fontWeight: '500',
                          lineHeight: '150%',
                          color: '#FFD2B5',
                          wordWrap: 'break-word'
                        }}>
                          보유 포인트
                        </span>
                        <span style={{
                          fontSize: isMobile ? '24px' : '28px',
                          fontWeight: '600',
                          lineHeight: isMobile ? '36px' : '42px',
                          color: 'var(--Color-text-inverse, white)',
                          wordWrap: 'break-word'
                        }}>
                          {formatPoints(currentPoints)}P
                        </span>
                      </div>

                      {/* 화살표 아이콘 */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <RightArrowIcon width={isMobile ? "1rem" : "1.25rem"} height={isMobile ? "1.05rem" : "1.3125rem"} color="#FFFFFF" /> 
                      </div>

                      {/* 수정 후 포인트 */}
                      <div style={{
                        width: isMobile ? 'auto' : '5.5625rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.125rem'
                      }}>
                        <span style={{
                          fontSize: isMobile ? '0.75rem' : '0.8125rem',
                          fontWeight: '500',
                          lineHeight: '150%',
                          color: '#FFD2B5',
                          wordWrap: 'break-word'
                        }}>
                          수정 후 포인트
                        </span>
                        <span style={{
                          fontSize: isMobile ? '1.5rem' : '1.75rem',
                          fontWeight: '600',
                          lineHeight: '150%',
                          color: 'var(--Color-text-inverse, white)',
                          wordWrap: 'break-word'
                        }}>
                          {formatPoints(afterPoints)}P
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 조정 컨트롤 */}
                <div style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem'
                }}>
                  {/* +/- 컨트롤 */}
                  <div style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: adjustment === 0 ? 'var(--Color-fill-inactive, #F5F5F5)' : 'var(--Color-fill-white, white)',
                    borderRadius: '0.5rem',
                    border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    {/* Minus 버튼 */}
                    <div 
                      style={{ 
                        width: '1.5rem', 
                        height: '1.5rem', 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onClick={() => setAdjustment(prev => prev - 1)}
                    >
                      <MinusIcon width="0.875rem" height="0.125rem" color="#161616" />
                    </div>

                    {/* 조정값 표시 */}
                    <input
                      type="number"
                      value={adjustment}
                      onChange={(e) => setAdjustment(Number(e.target.value))}
                      style={{
                        flex: '1',
                        textAlign: 'center',
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        color: adjustment === 0 ? 'var(--Color-text-assistive, #989898)' : 'var(--Color-text-normal, #161616)',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '19.5px'
                      }}
                    />

                    {/* Plus 버튼 */}
                    <div 
                      style={{ 
                        width: '24px', 
                        height: '24px', 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onClick={() => setAdjustment(prev => prev + 1)}
                    >
                      <PlusIcon width={24} height={24} fill="#0D0D0D" />
                    </div>
                  </div>

                  {/* 빠른 조정 버튼들 */}
                  <div style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '6px' : '8px',
                    flexWrap: isMobile ? 'wrap' : 'nowrap'
                  }}>
                    {[5, 10, 20, 50].map((amount) => (
                      <div 
                        key={amount}
                        style={{
                          flex: isMobile ? '1 1 calc(50% - 3px)' : '1',
                          minHeight: isMobile ? '36px' : '40px',
                          maxHeight: isMobile ? '36px' : '40px',
                          padding: isMobile ? '6px 10px' : '8px 12px',
                          background: 'var(--Color-fill-white, white)',
                          borderRadius: '8px',
                          border: '1px solid var(--Color-line-normal, #F0F0F0)',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onClick={() => quickAdjust(amount)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--Color-fill-hover, #F5F5F5)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'var(--Color-fill-white, white)';
                        }}
                      >
                        <span style={{
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: isMobile ? '13px' : '14px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: isMobile ? '19.5px' : '21px'
                        }}>
                          +{amount}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 버튼 영역 */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? '6px' : '8px',
                width: isMobile ? '100%' : 'auto'
              }}>
                <Button
                  width={isMobile ? "calc(50% - 3px)" : "124px"}
                  height={isMobile ? "40px" : "45px"}
                  variant="cancel"
                  onClick={handleReset}
                >
                  새로고침
                </Button>
                <Button
                  width={isMobile ? "calc(50% - 3px)" : "262px"}
                  height={isMobile ? "40px" : "45px"}
                  variant="submit"
                  onClick={handleComplete}
                >
                  완료
                </Button>
              </div>
            </div>

            {/* 적립 내역 영역 */}
            <div style={{
              width: '100%',
              padding: isMobile ? '1rem 1rem 0rem 1rem' : '1.5rem 1.75rem 0rem 1.75rem',
              borderTop: '0.375rem solid var(--Color-fill-hover, #F5F5F5)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: isMobile ? '16px' : '24px'
            }}>
              <h2 style={{
                width: '100%',
                margin: 0,
                fontSize: isMobile ? '15px' : '16px',
                fontWeight: '600',
                lineHeight: isMobile ? '22.5px' : '24px',
                color: 'var(--Color-text-normal, #161616)',
                wordWrap: 'break-word'
              }}>
                적립 내역
              </h2>

              {/* 내역 리스트 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '28px'
              }}>
                {Object.keys(groupedHistory).length > 0 ? (
                  Object.entries(groupedHistory).map(([month, items]) => (
                    <div key={month} style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px'
                    }}>
                      {/* 월 헤더 */}
                      <span style={{
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        lineHeight: '150%',
                        color: 'var(--Color-text-assistive, #989898)',
                        wordWrap: 'break-word'
                      }}>
                        {month}
                      </span>

                      {/* 해당 월의 내역들 */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1.75rem'
                      }}>
                        {items.map((item) => (
                          <div key={item.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            {/* 아이콘 (임시 박스) */}
                            <div style={{
                              width: isMobile ? '2.5rem' : '2.75rem',
                              height: isMobile ? '2.5rem' : '2.75rem',
                              border: '0.77px solid var(--Color-line-normal, #F0F0F0)',
                              borderRadius: '0.25rem',
                              flexShrink: 0
                            }} />

                            {/* 내역 정보 */}
                            <div style={{
                              width: isMobile ? 'calc(100% - 3.5rem)' : '21.4375rem',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start'
                            }}>
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.25rem'
                              }}>
                                <span style={{
                                  fontSize: '0.8125rem',
                                  fontWeight: '500',
                                  lineHeight: '150%',
                                  color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                                  wordWrap: 'break-word'
                                }}>
                                  {item.type === 'add' ? '적립' : item.type === 'subtract' ? '차감' : '사용'} ({item.reason})
                                </span>
                                <span style={{
                                  fontSize: '0.8125rem',
                                  fontWeight: '500',
                                  lineHeight: '150%',
                                  color: 'var(--Color-text-assistive, #989898)',
                                  wordWrap: 'break-word'
                                }}>
                                  {formatDate(item.createdAt)}
                                </span>
                              </div>

                              <div style={{
                                width: '2.5rem',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                gap: '0.25rem'
                              }}>
                                <span style={{
                                  textAlign: 'right',
                                  fontSize: '0.8125rem',
                                  fontWeight: '600',
                                  lineHeight: '150%',
                                  color: item.type === 'add' ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)',
                                  wordWrap: 'break-word'
                                }}>
                                  {item.type === 'add' || item.type === 'weekly' || item.type === 'monthly' ? '+' : '-'}{item.amount}P
                                </span>
                                <span style={{
                                  textAlign: 'right',
                                  fontSize: '0.75rem',
                                  fontWeight: '500',
                                  lineHeight: '150%',
                                  color: 'var(--Color-text-assistive, #989898)',
                                  wordWrap: 'break-word'
                                }}>
                                  {formatPoints(item.balance || 0)}P
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{
                    padding: '1.25rem',
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: '0.875rem'
                  }}>
                    적립 내역이 없습니다.
                  </div>
                )}
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentPointEditDialog;

